/**
 * @fileoverview Componente responsável por gerenciar filtros, abas e listagens de tickets.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import GroupIcon from "@material-ui/icons/Group";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import BulkActionsModal from "../BulkActionsModal";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem, Checkbox, ListItemText } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import FilterListIcon from "@material-ui/icons/FilterList";
import { Eye, Plus, Search, CheckCircle, CheckCheck } from "lucide-react";
const useStyles = makeStyles(theme => ({
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		width: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		borderRadius:0,
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(22, 22, 24, 0.95) 0%, rgba(26, 26, 28, 0.95) 100%)'
			: '#FFFFFF',
	},

	tabsHeader: {
		flex: "none",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.tabHeaderBackground,
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.15)'
			: '1px solid rgba(0, 0, 0, 0.12)',
		padding: '12px 16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		minHeight: '48px',
		flexWrap: 'wrap',
		gap: '6px',
		[theme.breakpoints.down('md')]: {
			padding: '12px 12px',
		},
		[theme.breakpoints.down('sm')]: {
			padding: '10px 10px',
			gap: '4px',
		},
	},

	tabsInternal: {
		flex: "none",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.tabHeaderBackground,
	},

	settingsIcon: {
		alignSelf: "center",
		marginLeft: "auto",
		padding: 8,
	},

	tabsContainer: {
		display: 'inline-flex',
		alignItems: 'stretch',
		backgroundColor: theme.mode === 'dark' ? 'rgba(42, 42, 42, 0.9)' : '#ffffff',
		borderRadius: '999px',
		padding: '3px',
		width: 'auto',
		minWidth: 0,
		height: '32px',
		boxSizing: 'border-box',
		border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid #f0f0f0',
		flexShrink: 1,
		overflow: 'hidden',
		[theme.breakpoints.down('sm')]: {
			height: '28px',
			padding: '2px',
		},
		'& .MuiTabs-root': {
			minHeight: 0,
			height: '100%',
			width: '100%',
		},
		'& .MuiTabs-scroller': {
			height: '100%',
		},
		'& .MuiTabs-flexContainer': {
			height: '100%',
			alignItems: 'stretch',
			gap: '2px',
		},
	},

	tab: {
		flex: '0 1 auto',
		minWidth: 0,
		minHeight: 0,
		width: 'auto',
		height: '100%',
		padding: '0 12px',
		margin: '0',
		boxSizing: 'border-box',
		borderRadius: '999px',
		backgroundColor: 'transparent',
		color: theme.mode === 'dark' ? '#b0b0b0' : '#666',
		fontWeight: 500,
		fontSize: '0.85rem',
		lineHeight: 1,
		textTransform: 'none',
		whiteSpace: 'nowrap',
		transition: 'background-color 150ms ease, color 150ms ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		[theme.breakpoints.down('sm')]: {
			padding: '0 9px',
			fontSize: '0.78rem',
		},
		'&:hover': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
			color: theme.mode === 'dark' ? '#e0e0e0' : '#333',
		},
		'&.Mui-selected': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(58, 58, 58, 0.95)' : '#f0f0f0',
			color: theme.mode === 'dark' ? '#e0e0e0' : '#333333',
			fontWeight: 600,
		},
		'& .MuiTab-wrapper': {
			fontSize: 'inherit',
			fontWeight: 'inherit',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: '100%',
		},
	},

	internalTab: {
		minWidth: 120,
		width: 120,
		padding: 5
	},

	searchButton: {
		marginLeft: '12px',
		padding: '8px',
		color: theme.palette.mode === 'light' ? '#666' : '#ccc',
		'&:hover': {
			backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#404040',
			borderRadius: '50%',
		}
	},

	ticketOptionsBox: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(18, 18, 20, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
			: theme.palette.optionsBackground,
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.1)'
			: '1px solid rgba(0, 0, 0, 0.12)',
		padding: theme.spacing(1),
		fontSize: '0.75rem',
		'& .MuiButton-root': {
			fontSize: '0.7rem',
			padding: '4px 4px',
			minHeight: '28px'
		},
		'& .MuiFormControlLabel-root': {
			'& .MuiFormControlLabel-label': {
				fontSize: '0.7rem',
			}
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem'
		},
		'& .MuiFormControl-root': {
			'& .MuiSelect-root': {
				fontSize: '0.8rem',
				minHeight: '28px',
				padding: '4px 4px',
				textAlign: 'center',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'start'
			},
			'& .MuiOutlinedInput-input': {
				padding: '4px 4px',
				paddingLeft: '8px',
				textAlign: 'center'
			}
		}
	},

	ticketSearchLine: {
		padding: theme.spacing(1),
	},

	serachInputWrapper: {
		flex: 1,
		background: theme.palette.total,
		display: "flex",
		borderRadius: 40,
		padding: 4,
		marginRight: theme.spacing(1),
	},

	searchIcon: {
		color: "grey",
		marginLeft: 6,
		marginRight: 6,
		alignSelf: "center",
		width: 16,
		height: 16,
		strokeWidth: 1.75,
		flexShrink: 0,
	},

	searchInput: {
		flex: 1,
		border: "none",
		borderRadius: 30,
		fontSize: '0.85rem',
		'& input': {
			fontSize: '0.85rem',
			padding: '6px 0',
			'&::placeholder': {
				fontSize: '0.85rem',
				opacity: 0.7,
			},
		},
	},

	insiderTabPanel: {
		height: '100%',
		marginTop: "-72px",
		paddingTop: "72px",
		overflow: "hidden",
	},

	insiderDoubleTabPanel: {
		display:"flex",
		flexDirection: "column",
		marginTop: "-72px",
		paddingTop: "72px",
		height: "100%",
		overflow: "hidden",
	},

	ticketsListContainer: {
		width: "100%",
		height: "100%",
		overflow: "auto",
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},

	ticketsListInner: {
		display: "block",
		width: "100%",
		height: "100%",
		overflow: "auto",
	},

	labelContainer: {
		width: "auto",
		padding: 0
	},
	iconLabelWrapper: {
		flexDirection: "row",
		'& > *:first-child': {
			marginBottom: '3px !important',
			marginRight: 16
		}
	},
	insiderTabLabel: {
		[theme.breakpoints.down(1600)]: {
			display:'none'
		}
	},
	smallFormControl: {
		'& .MuiOutlinedInput-input': {
			padding: "12px 10px",
		},
		'& .MuiInputLabel-outlined': {
			marginTop: "-6px"
		}
	},

	subTab: {
		minHeight: '48px',
		padding: '12px 24px',
		fontSize: '0.7rem',
		fontWeight: 500,
		marginRight: '24px',
		'& .MuiTab-wrapper': {
			fontSize: '0.7rem',
			fontWeight: 500,
		},
		'& .MuiBadge-root': {
			fontSize: '0.7rem',
		},
		'& .MuiBadge-badge': {
			fontSize: '0.65rem',
			height: '18px',
			minWidth: '18px',
		}
	},

	badge: {
		'& .MuiBadge-badge': {
			color: '#fff',
			backgroundColor: '#6EC263',
			minWidth: '20px',
			height: '20px',
			borderRadius: '10px',
			padding: '0 6px',
			fontSize: '0.75rem'
		}
	},
	
	newTicketButton: {
		width: 40,
		height: 40,
		minWidth: 40,
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
		}
	},

	floatingPlusButton: {
		position: 'absolute',
		bottom: '24px',
		left: '24px',
		width: 56,
		height: 56,
		minWidth: 56,
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
		zIndex: 1000,
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
			boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
			transform: 'scale(1.05)',
		},
		'& .MuiButton-label': {
			padding: 0,
			fontSize: '1.5rem',
			fontWeight: 300,
		},
		transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},

	iconsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		marginLeft: 'auto',
		flexShrink: 0,
		[theme.breakpoints.down('sm')]: {
			gap: '2px',
		},
	},

	clickableIcon: {
		padding: '4px',
		width: '28px',
		height: '28px',
		minWidth: '28px',
		borderRadius: '4px',
		border: 'none',
		color: '#666666',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
		[theme.breakpoints.down('sm')]: {
			padding: '3px',
			width: '24px',
			height: '24px',
			minWidth: '24px',
			'& svg': {
				width: '14px',
				height: '14px',
			},
		},
		'&:hover': {
			backgroundColor: '#f8f8f8',
			color: '#333333',
			borderColor: '#e0e0e0',
		},
		'&.active': {
			backgroundColor: 'transparent',
			color: theme.palette.primary.main,
			borderColor: theme.palette.primary.main,
		},
		'&.active:hover': {
			backgroundColor: '#f8f8f8',
			color: theme.palette.primary.dark,
			borderColor: theme.palette.primary.dark,
		},
		'& svg': {
			width: '16px',
			height: '16px',
			strokeWidth: 1.75,
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem',
		},
	},

	searchIconButton: {
		padding: '4px',
		width: '28px',
		height: '28px',
		minWidth: '28px',
		borderRadius: '4px',
		border: 'none',
		color: '#444444',
		backgroundColor: 'transparent',
		transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: '#f8f8f8',
			color: '#222222',
			borderColor: '#e0e0e0',
		},
		'& svg': {
			width: '16px',
			height: '16px',
			strokeWidth: 1.75,
		},
		'& .MuiSvgIcon-root': {
			fontSize: '1rem',
		},
	},

	unifiedFiltersContainer: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		'& > *': {
			width: '100%',
		},
	},
	filterDropdownFormControl: {
		width: '100%',
	},
	filterDropdownSelect: {
		backgroundColor: theme.mode === 'light' ? '#f8fafc' : 'rgba(39, 39, 42, 0.95)',
		borderRadius: '14px',
		transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
		fontWeight: 500,
		cursor: 'pointer',
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.mode === 'light' ? '#e2e8f0' : 'rgba(91, 124, 255, 0.3)',
			borderWidth: '1.5px',
			transition: 'border-color 0.2s ease',
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.mode === 'light' ? theme.palette.primary.main : 'rgba(91, 124, 255, 0.7)',
			boxShadow: 'none',
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.palette.primary.main,
			borderWidth: '2px',
			boxShadow: theme.mode === 'dark'
				? '0 0 30px rgba(91, 124, 255, 0.3), 0 0 10px rgba(124, 58, 237, 0.2), inset 0 0 15px rgba(91, 124, 255, 0.1)'
				: '0 0 0 4px rgba(61, 92, 253, 0.1)',
		},
		'& .MuiSelect-select': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			padding: '14px 16px',
			cursor: 'pointer',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-start',
			textAlign: 'left',
			fontWeight: 500,
			'&:focus': {
				backgroundColor: 'transparent',
				borderRadius: '14px',
			},
		},
		'& .MuiSvgIcon-root': {
			color: theme.mode === 'light' ? '#64748b' : '#5B7CFF',
		},
	},
	filterDropdownTextField: {
		'& .MuiOutlinedInput-root': {
			backgroundColor: theme.mode === 'light' ? '#f8fafc' : 'rgba(39, 39, 42, 0.95)',
			borderRadius: '14px',
			transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.mode === 'light' ? '#e2e8f0' : 'rgba(91, 124, 255, 0.3)',
				borderWidth: '1.5px',
				transition: 'border-color 0.2s ease',
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.mode === 'light' ? theme.palette.primary.main : 'rgba(91, 124, 255, 0.7)',
				boxShadow: 'none',
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: theme.palette.primary.main,
				borderWidth: '2px',
				boxShadow: theme.mode === 'dark'
					? '0 0 30px rgba(91, 124, 255, 0.3), 0 0 10px rgba(124, 58, 237, 0.2), inset 0 0 15px rgba(91, 124, 255, 0.1)'
					: '0 0 0 4px rgba(61, 92, 253, 0.1)',
			},
		},
		'& .MuiAutocomplete-inputRoot': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			padding: '4px 12px',
			minHeight: '50px',
			alignItems: 'center',
			justifyContent: 'flex-start',
			textAlign: 'left',
		},
		'& .MuiInputBase-input': {
			color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
			fontWeight: 500,
			textAlign: 'left',
			'&::placeholder': {
				color: theme.mode === 'light' ? '#1e293b' : '#FFFFFF',
				opacity: 1,
			},
		},
		'& .MuiSvgIcon-root': {
			color: theme.mode === 'light' ? '#64748b' : '#5B7CFF',
		},
	},
	filterDropdownAutocompleteRoot: {
		width: '100%',
	},
	filterDropdownAutocompletePopper: {
		zIndex: 10000,
	},
	filterDropdownAutocompleteOption: {
		color: theme.mode === 'dark' ? '#F5F5F5' : '#1e293b',
		fontSize: '0.875rem',
		padding: '10px 14px',
		borderRadius: '8px',
		margin: '2px 4px',
		minHeight: 'auto',
		transition: 'background-color 0.2s ease, color 0.2s ease',
		'&[data-focus="true"]': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.15)' : 'rgba(61, 92, 253, 0.08)',
		},
		'&[aria-selected="true"]': {
			backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.25)' : 'rgba(61, 92, 253, 0.12)',
		},
	},
	filterDropdownMenuPaper: {
		backgroundColor: theme.mode === 'dark' ? 'rgba(24, 24, 27, 1) !important' : '#ffffff',
		borderRadius: '12px',
		border: theme.mode === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid #e2e8f0',
		marginTop: '4px',
		maxHeight: '250px !important',
		overflowY: 'auto',
		zIndex: 9999,
		boxShadow: theme.mode === 'dark'
			? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(91, 124, 255, 0.2)'
			: '0 4px 12px rgba(0, 0, 0, 0.1)',
		'& .MuiList-root': {
			padding: '8px !important',
		},
		'& .MuiMenuItem-root': {
			color: theme.mode === 'dark' ? '#F5F5F5' : '#1e293b',
			fontSize: '0.875rem',
			padding: '10px 14px !important',
			borderRadius: '8px',
			margin: '2px 4px !important',
			minHeight: 'auto !important',
			transition: 'background-color 0.2s ease, color 0.2s ease',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.15)' : 'rgba(61, 92, 253, 0.08)',
			},
			'&.Mui-selected': {
				backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.25)' : 'rgba(61, 92, 253, 0.12)',
				'&:hover': {
					backgroundColor: theme.mode === 'dark' ? 'rgba(91, 124, 255, 0.35)' : 'rgba(61, 92, 253, 0.16)',
				},
			},
		},
	},
	connectionOptionName: {
		fontWeight: 500,
	},
	connectionStatusRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		fontSize: '0.75rem',
		color: theme.mode === 'dark' ? '#a1a1aa' : '#64748b',
	},
	connectionStatusDot: {
		width: '8px',
		height: '8px',
		borderRadius: '50%',
		flexShrink: 0,
	},
	filterDialog: {
		'& .MuiDialog-paper': {
			background: theme.palette.type === 'dark'
				? 'linear-gradient(135deg, rgba(18, 18, 19, 1) 0%, rgba(22, 22, 24, 1) 100%)'
				: '#ffffff',
			border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
			borderRadius: '24px',
			boxShadow: theme.palette.type === 'dark'
				? '0 8px 32px rgba(0, 0, 0, 0.4)'
				: '0 8px 32px rgba(0, 0, 0, 0.1)',
			width: '420px',
			maxWidth: '90vw',
		},
	},
	filterDialogTitle: {
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(91, 124, 255, 0.1) 0%, rgba(157, 124, 255, 0.1) 100%)'
			: 'linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(138, 35, 135, 0.05) 100%)',
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.2)'
			: '1px solid rgba(0, 0, 0, 0.08)',
		color: theme.palette.type === 'dark' ? '#ffffff' : '#1a1a1a',
		fontWeight: 600,
		padding: '16px 20px 12px 20px',
		fontSize: '1.125rem',
	},
	filterDialogContent: {
		padding: '16px 20px',
	},
	filterDialogActions: {
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(15, 15, 17, 0.5) 0%, rgba(20, 20, 22, 0.5) 100%)'
			: '#fafafa',
		borderTop: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.2)'
			: '1px solid rgba(0, 0, 0, 0.08)',
		padding: '12px 20px 16px 20px',
		gap: '8px',
	},
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [showSetoresModal, setShowSetoresModal] = useState(false);
  const searchInputRef = useRef();
  const searchTimeoutRef = useRef(null);
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { currentTicket } = useContext(TicketsContext);
  const { profile } = user;

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  // Tanto admin quanto user enxergam tickets "Sem fila" por padrão (sentinela
  // 0 do TicketsQueueSelect). A opção sempre apareceu no dropdown pra todos,
  // só vinha desmarcada pra user comum — agora vem marcada também, fica
  // explícito e o atendente vê tickets sem fila atribuída sem precisar
  // marcar manualmente a opção.
  const initialQueueIds = [...userQueueIds, 0];
  const [selectedQueueIds, setSelectedQueueIds] = useState(initialQueueIds);
  const [selectedTags, setSelectedTags] = useState([]);
  const [withoutTags, setWithoutTags] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);

  // Estados temporários para filtros (antes de aplicar)
  const [tempSelectedQueueIds, setTempSelectedQueueIds] = useState(initialQueueIds);
  const [tempSelectedTags, setTempSelectedTags] = useState([]);
  const [tempWithoutTags, setTempWithoutTags] = useState(false);
  const [tempSelectedUsers, setTempSelectedUsers] = useState([]);
  const [tempSelectedWhatsappIds, setTempSelectedWhatsappIds] = useState([]);

  const [unreadOnly, setUnreadOnly] = useState(false);

  // Estados para ações em massa
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState("accept"); // "accept" ou "resolve"

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // User comum: selectedUsers fica [] no mount — backend trata showAll=false
    // com Op.or [{userId},{status:pending}], preservando visibilidade da fila
    // pending. UsersFilter (liberado pra user) permite filtrar sob demanda.
  }, []);

  // Sincronizar aba com o status do ticket atual
  // Quando um ticket é reaberto (code: "#open"), mudar para aba "open"
  useEffect(() => {
    if (currentTicket?.code === "#open") {
      setTab("open");
      setTabOpen("open");
    }
  }, [currentTicket]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
  }, [tab]);

  const handleSearch = useCallback((e) => {
    const searchedTerm = e.target.value.toLowerCase();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleChangeTab = useCallback((e, newValue) => {
    setTab(newValue);
  }, []);

  const handleChangeTabOpen = useCallback((e, newValue) => {
    setTabOpen(newValue);
  }, []);

  const handleCloseOrOpenTicket = useCallback((ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  }, [history]);

  // Handlers temporários (não aplicam o filtro imediatamente)
  // Recebe { tagIds, withoutTags } do TagsFilter (popover) e atualiza ambos temps.
  const handleTempSelectedTags = useCallback(({ tagIds, withoutTags: wt }) => {
    setTempSelectedTags(tagIds);
    setTempWithoutTags(wt);
  }, []);

  const handleTempSelectedUsers = useCallback((selecteds) => {
    const users = selecteds.map((t) => t.id);
    setTempSelectedUsers(users);
  }, []);

  const normalizeMultiSelectValues = (values) => {
    const rawValues = Array.isArray(values) ? values : String(values).split(",");
    return rawValues
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  };

  const getConnectionStatusInfo = (status) => {
    const normalized = String(status || "").toUpperCase();
    const connectedStates = new Set(["CONNECTED", "WORKING", "PAIRING"]);
    const isConnected = connectedStates.has(normalized);

    return {
      label: isConnected ? "Conectada" : "Desconectada",
      color: isConnected ? "#22c55e" : "#ef4444",
    };
  };

  const filterAutocompleteClasses = useMemo(
    () => ({
      root: classes.filterDropdownAutocompleteRoot,
      popper: classes.filterDropdownAutocompletePopper,
      paper: classes.filterDropdownMenuPaper,
      option: classes.filterDropdownAutocompleteOption,
    }),
    [classes]
  );

  // Aplicar filtros (quando clicar no botão Aplicar)
  const handleApplyFilters = useCallback(() => {
    // Sem nenhuma fila marcada (nem "Sem fila") a query cai num `queueId IN ()`
    // e o backend acaba devolvendo tudo — bloqueamos no cliente com aviso.
    if (!tempSelectedQueueIds || tempSelectedQueueIds.length === 0) {
      toast.warn(i18n.t("tickets.filters.emptyQueueWarning"));
      return;
    }
    setSelectedQueueIds(tempSelectedQueueIds);
    setSelectedTags(tempSelectedTags);
    setWithoutTags(tempWithoutTags);
    setSelectedUsers(tempSelectedUsers);
    setSelectedWhatsappIds(tempSelectedWhatsappIds);
    setShowSetoresModal(false);
  }, [tempSelectedQueueIds, tempSelectedTags, tempWithoutTags, tempSelectedUsers, tempSelectedWhatsappIds]);

  // Cancelar filtros (quando clicar no botão Cancelar)
  const handleCancelFilters = useCallback(() => {
    // Reseta os temporários para os valores aplicados
    setTempSelectedQueueIds(selectedQueueIds);
    setTempSelectedTags(selectedTags);
    setTempWithoutTags(withoutTags);
    setTempSelectedUsers(selectedUsers);
    setTempSelectedWhatsappIds(selectedWhatsappIds);
    setShowSetoresModal(false);
  }, [selectedQueueIds, selectedTags, withoutTags, selectedUsers, selectedWhatsappIds]);

  // Quando abrir o modal, sincroniza temp com valores aplicados
  const handleOpenFiltersModal = useCallback(() => {
    setTempSelectedQueueIds(selectedQueueIds);
    setTempSelectedTags(selectedTags);
    setTempWithoutTags(withoutTags);
    setTempSelectedUsers(selectedUsers);
    setTempSelectedWhatsappIds(selectedWhatsappIds);
    setShowSetoresModal(true);
  }, [selectedQueueIds, selectedTags, withoutTags, selectedUsers, selectedWhatsappIds]);

  // Funções para ações em massa
  const handleOpenBulkAccept = useCallback(() => {
    setBulkActionType("accept");
    setBulkModalOpen(true);
  }, []);

  const handleOpenBulkResolve = useCallback(() => {
    setBulkActionType("resolve");
    setBulkModalOpen(true);
  }, []);

  return (
    <div className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <BulkActionsModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        actionType={bulkActionType}
      />
      <div className={classes.tabsHeader}>
        <div className={classes.tabsContainer}>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            variant="standard"
            TabIndicatorProps={{ style: { display: "none" } }}
            textColor="inherit"
            aria-label="icon label tabs example"
            style={{ minHeight: 'auto' }}
          >
            <Tab
              value={"open"}
              label={i18n.t("tickets.tabs.open.title")}
              classes={{ root: classes.tab }}
            />
            <Tab
              value={"groups"}
              label={i18n.t("tickets.tabs.groups.title")}
              classes={{ root: classes.tab }}
            />
            <Tab
              value={"closed"}
              label={i18n.t("tickets.tabs.closed.title")}
              classes={{ root: classes.tab }}
            />
          </Tabs>
        </div>
        <div className={classes.iconsContainer}>
          <Tooltip title="Não lidas" placement="bottom">
            <IconButton
              className={`${classes.clickableIcon} ${unreadOnly ? 'active' : ''}`}
              onClick={() => setUnreadOnly(prev => !prev)}
            >
              <NotificationsNoneIcon />
            </IconButton>
          </Tooltip>
          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <Tooltip title="Todos" placement="bottom">
                <IconButton
                  className={`${classes.clickableIcon} ${showAllTickets ? 'active' : ''}`}
                  onClick={() => setShowAllTickets((prevState) => !prevState)}
                >
                  <Eye />
                </IconButton>
              </Tooltip>
            )}
          />
          <Tooltip title="Filtros" placement="bottom">
            <IconButton
              className={classes.clickableIcon}
              onClick={handleOpenFiltersModal}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          {/* Botões de ação em massa - OCULTADOS
          <Tooltip title="Aceitar em massa" placement="bottom">
            <IconButton
              className={classes.clickableIcon}
              onClick={handleOpenBulkAccept}
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resolver em massa" placement="bottom">
            <IconButton
              className={classes.clickableIcon}
              onClick={handleOpenBulkResolve}
            >
              <CheckCheck />
            </IconButton>
          </Tooltip>
          */}

          <Tooltip title="Buscar tickets" placement="bottom">
            <IconButton
              className={classes.searchIconButton}
              onClick={() => setTab("search")}
            >
              <Search />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {tab === "search" && (
        <div className={classes.ticketOptionsBox}>
          <div className={classes.serachInputWrapper}>
            <Search className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        </div>
      )}

      <Button
        className={classes.floatingPlusButton}
        onClick={() => setNewTicketModalOpen(true)}
      >
        <Plus />
      </Button>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={openCount}
                color="primary"
              >
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value={"open"}
            classes={{ root: classes.subTab }}
          />
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={pendingCount}
                color="primary"
              >
                {i18n.t("ticketsList.pendingHeaderTab")}
              </Badge>
            }
            value={"pending"}
            classes={{ root: classes.subTab }}
          />
        </Tabs>
        <div className={classes.ticketsListContainer}>
          <div className={classes.ticketsListInner} style={{ display: tabOpen === "open" ? "block" : "none" }}>
            <TicketsList
              status="open"
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              selectedWhatsappIds={selectedWhatsappIds}
              updateCount={(val) => setOpenCount(val)}
              withUnreadMessages={tabOpen === "open" && unreadOnly ? "true" : undefined}
              disableItemRendering={tabOpen !== "open"}
              tags={selectedTags}
              withoutTags={withoutTags}
              users={selectedUsers}
            />
          </div>
          <div className={classes.ticketsListInner} style={{ display: tabOpen === "pending" ? "block" : "none" }}>
            <TicketsList
              status="pending"
              selectedQueueIds={selectedQueueIds}
              selectedWhatsappIds={selectedWhatsappIds}
              updateCount={(val) => setPendingCount(val)}
              withUnreadMessages={tabOpen === "pending" && unreadOnly ? "true" : undefined}
              disableItemRendering={tabOpen !== "pending"}
              tags={selectedTags}
              withoutTags={withoutTags}
              users={selectedUsers}
            />
          </div>
        </div>
      </TabPanel>
      <TabPanel value={tab} name="groups" className={classes.ticketsWrapper}>
        <TicketsList
          status="groups"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedWhatsappIds={selectedWhatsappIds}
          withUnreadMessages={tab === "groups" && unreadOnly ? "true" : undefined}
          tags={selectedTags}
          withoutTags={withoutTags}
          users={selectedUsers}
        />
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedWhatsappIds={selectedWhatsappIds}
          tags={selectedTags}
          withoutTags={withoutTags}
          users={selectedUsers}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          tags={selectedTags}
          withoutTags={withoutTags}
          users={selectedUsers}
          selectedWhatsappIds={selectedWhatsappIds}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>

      <Dialog
        open={showSetoresModal}
        onClose={handleCancelFilters}
        maxWidth={false}
        className={classes.filterDialog}
      >
        <DialogTitle className={classes.filterDialogTitle}>
          Filtros
        </DialogTitle>

        <DialogContent className={classes.filterDialogContent}>
          <div className={classes.unifiedFiltersContainer}>
            <FormControl
              fullWidth
              margin="dense"
              variant="outlined"
              className={classes.filterDropdownFormControl}
            >
              <Select
                multiple
                displayEmpty
                className={classes.filterDropdownSelect}
                value={tempSelectedWhatsappIds}
                onChange={(e) => setTempSelectedWhatsappIds(normalizeMultiSelectValues(e.target.value))}
                MenuProps={{
                  classes: { paper: classes.filterDropdownMenuPaper },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => i18n.t("tickets.filters.connection")}
              >
                {(whatsApps || []).map((whatsapp) => {
                  const statusInfo = getConnectionStatusInfo(whatsapp.status);

                  return (
                  <MenuItem dense key={whatsapp.id} value={whatsapp.id}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={tempSelectedWhatsappIds.indexOf(whatsapp.id) > -1}
                    />
                    <ListItemText
                      primary={<span className={classes.connectionOptionName}>{whatsapp.name}</span>}
                      secondary={
                        <span className={classes.connectionStatusRow}>
                          <span
                            className={classes.connectionStatusDot}
                            style={{ backgroundColor: statusInfo.color }}
                          />
                          {statusInfo.label}
                        </span>
                      }
                    />
                  </MenuItem>
                )})}
              </Select>
            </FormControl>

            <TicketsQueueSelect
              selectedQueueIds={tempSelectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setTempSelectedQueueIds(values)}
              wrapperStyle={{ width: "100%", marginTop: 0 }}
              formControlClassName={classes.filterDropdownFormControl}
              selectClassName={classes.filterDropdownSelect}
              menuPaperClassName={classes.filterDropdownMenuPaper}
              placeholderText={i18n.t("tickets.filters.queues")}
            />

            <div style={{ marginTop: 8, marginBottom: 4 }}>
              <TagsFilter
                selectedTagIds={tempSelectedTags}
                withoutTags={tempWithoutTags}
                onChange={handleTempSelectedTags}
                placeholder={i18n.t("tickets.filters.tagsPlaceholder")}
                title={i18n.t("tickets.filters.tagsPopoverTitle")}
                searchPlaceholder={i18n.t("tickets.filters.tagsSearchPlaceholder")}
                noTagsLabel={i18n.t("tickets.filters.noTagsOption")}
                noTagsFoundLabel={i18n.t("tickets.filters.noTagsFound")}
                singleSelectedLabel={i18n.t("tickets.filters.singleSelected")}
                multipleSelectedLabel={i18n.t("tickets.filters.multipleSelected")}
                showingAllLabel={i18n.t("tickets.filters.showingAll")}
                clearLabel={i18n.t("tickets.filters.clear")}
                loadingLabel={i18n.t("tickets.filters.loadingTags")}
                fullWidth
              />
            </div>

            <UsersFilter
              onFiltered={handleTempSelectedUsers}
              initialUsers={tempSelectedUsers}
              autocompleteClasses={filterAutocompleteClasses}
              textFieldClassName={classes.filterDropdownTextField}
              placeholderText="Filtrar por Usuários"
              containerStyle={{ padding: 0, marginTop: 8, marginBottom: 4 }}
            />
          </div>
        </DialogContent>

        <DialogActions className={classes.filterDialogActions}>
          <Button
            onClick={handleCancelFilters}
            style={{
              color: '#6b7280',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '6px 16px',
              borderRadius: '6px'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            color="primary"
            style={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              borderRadius: '6px',
              padding: '6px 16px',
              boxShadow: 'none'
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketsManagerTabs;
